import { useState, useEffect } from 'react';
import { VIEW_MODES, VIEW_MODE_BREAKPOINTS } from '../constants';
import { hostWindow } from '../../shared/host_window';
import { subscribeMediaQuery } from './useMediaQuery';

// The tiers are read through matchMedia instead of comparing window.innerWidth
// in a resize listener: the queries mirror the module's sass breakpoints
// exactly (same CSS engine, same rounding), and the change listeners fire only
// when a boundary is crossed — dragging the window inside one tier produces no
// events, so breakpoint flapping can no longer burst list refetches.
const buildQueries = () => ({
  mobile: window.matchMedia(`(max-width: ${VIEW_MODE_BREAKPOINTS.MOBILE_MAX_WIDTH}px)`),
  landscape: window.matchMedia(`(max-width: ${VIEW_MODE_BREAKPOINTS.LANDSCAPE_MAX_WIDTH}px)`),
});

// The window whose viewport describes the device. On the portal the leaderboard
// runs inside an iframe that widgets.init_iframe stretches to the full content
// height, so the frame's own viewport is portrait and thousands of pixels tall —
// a height test there can never match. The parent's viewport is the real one,
// when it is ours to read (shared/host_window).
//
// A phone on its side, identified by its short edge rather than its width:
// modern iPhones are 844-956px wide in landscape, past LANDSCAPE_MAX_WIDTH, so
// the width tiers alone read them as web. Measured rather than matchMedia'd,
// because media queries only ever see the current document's viewport.
const isPhoneLandscape = () => {
  const viewport = hostWindow();
  return viewport.innerWidth > viewport.innerHeight &&
    viewport.innerHeight <= VIEW_MODE_BREAKPOINTS.PHONE_MAX_HEIGHT;
};

const getViewMode = (queries) => {
  if (queries.mobile.matches) return VIEW_MODES.MOBILE;
  if (isPhoneLandscape()) return VIEW_MODES.LANDSCAPE;
  if (queries.landscape.matches) return VIEW_MODES.LANDSCAPE;
  return VIEW_MODES.WEB;
};

const useViewMode = () => {
  const [queries] = useState(buildQueries);
  const [viewMode, setViewMode] = useState(() => getViewMode(queries));

  useEffect(() => {
    const onChange = () => setViewMode(getViewMode(queries));
    const unsubscribers = Object.values(queries).map((mql) => subscribeMediaQuery(mql, onChange));

    // isPhoneLandscape measures a viewport instead of matching a media query, so
    // nothing above fires when the device turns — least of all when the viewport
    // being measured belongs to the parent window.
    const viewport = hostWindow();
    viewport.addEventListener('resize', onChange);
    viewport.addEventListener('orientationchange', onChange);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      viewport.removeEventListener('resize', onChange);
      viewport.removeEventListener('orientationchange', onChange);
    };
  }, [queries]);

  return viewMode;
};

export default useViewMode;
